import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { Icon } from '../ui/icons.js';
import {
  applyFix,
  byteToChar,
  diagnosticsAt,
  localCompletions,
  scopeNames,
  tokenizeExpr,
  wordAt,
  type Completion,
  type ProcgenDiagnostic,
  type ProcgenDoc,
} from './procgen-doc.js';
import { completeRequest, engineComplete, engineFormat, engineHover, procgenWorkerEngine } from './procgen-preview.js';

/**
 * One expression slot of a generator (section 5): syntax colouring,
 * autocomplete (`ProcgenPreview.complete`, else local names), hover / signature
 * help for the token at the caret, squiggles from diagnostic spans, quick
 * fixes, Format (the engine's canonical printer when the bundle has one) and
 * "show where true" (previews the expression).
 *
 * The text is committed on blur, Ctrl+Enter and after a short pause in typing.
 */

const COMMIT_DELAY_MS = 700;
const MAX_ITEMS = 12;

interface Menu {
  items: Completion[];
  index: number;
  start: number;
  end: number;
}

export interface ExpressionFieldProps {
  value: string;
  onChange: (text: string) => void;
  /** JSON path of the slot (`outputs.trees.where[0]`), for diagnostics and completion. */
  path: string;
  /** The generator file. */
  file: string;
  doc: ProcgenDoc | null;
  diagnostics: readonly ProcgenDiagnostic[];
  /** The output this slot belongs to (local completions of its lets / attributes). */
  outputName?: string;
  /** "Show where true": preview this expression. */
  onShow?: (text: string) => void;
  placeholder?: string;
  /** Extra controls after the built-in buttons. */
  actions?: ReactNode;
}

/** Character ranges of the squiggles of this slot. */
function squiggles(text: string, diags: readonly ProcgenDiagnostic[]): { start: number; end: number; severity: string }[] {
  return diags.map((d) => {
    if (!d.span) return { start: 0, end: text.length, severity: d.severity };
    const start = byteToChar(text, d.span[0]);
    const end = Math.max(start + 1, byteToChar(text, d.span[1]));
    return { start, end: Math.min(text.length, end), severity: d.severity };
  });
}

/** The highlighted copy of the text drawn under the transparent textarea. */
function Highlight({ text, marks }: { text: string; marks: { start: number; end: number; severity: string }[] }) {
  const tokens = useMemo(() => tokenizeExpr(text), [text]);
  const cuts = new Set<number>([0, text.length]);
  for (const t of tokens) {
    cuts.add(t.start);
    cuts.add(t.end);
  }
  for (const m of marks) {
    cuts.add(Math.max(0, Math.min(text.length, m.start)));
    cuts.add(Math.max(0, Math.min(text.length, m.end)));
  }
  const points = [...cuts].sort((a, b) => a - b);
  const pieces: ReactNode[] = [];
  let ti = 0;
  for (let i = 0; i + 1 < points.length; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (b <= a) continue;
    while (ti < tokens.length && tokens[ti]!.end <= a) ti++;
    const token = tokens[ti];
    const mark = marks.find((m) => m.start <= a && b <= m.end);
    const cls = [`pcg-tok-${token?.kind ?? 'space'}`, mark ? `pcg-squiggle is-${mark.severity}` : ''].filter(Boolean).join(' ');
    pieces.push(
      <span key={a} className={cls}>
        {text.slice(a, b)}
      </span>,
    );
  }
  // A trailing newline needs a character after it to keep the heights equal.
  return <>{pieces}{'​'}</>;
}

export function ExpressionField({ value, onChange, path, file, doc, diagnostics, outputName, onShow, placeholder, actions }: ExpressionFieldProps) {
  const [text, setText] = useState(value);
  const [focused, setFocused] = useState(false);
  const [menu, setMenu] = useState<Menu | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const hlRef = useRef<HTMLPreElement | null>(null);
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const request = useRef(0);
  const latest = useRef({ text, value, onChange });
  latest.current = { text, value, onChange };

  useEffect(() => {
    if (!focused) setText(value);
  }, [value, focused]);

  useEffect(
    () => () => {
      if (commitTimer.current) clearTimeout(commitTimer.current);
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      // Unmounting mid-edit (the slot disappeared) still keeps the text.
      const l = latest.current;
      if (l.text !== l.value) l.onChange(l.text);
    },
    [],
  );

  const own = useMemo(() => diagnosticsAt(diagnostics, file, path), [diagnostics, file, path]);
  // Spans index the text the engine checked: only draw them while it is unchanged.
  const marks = text === value ? squiggles(text, own) : [];
  const worst = own.some((d) => d.severity === 'error') ? 'error' : own.some((d) => d.severity === 'warning') ? 'warning' : null;

  function commit(next = latest.current.text): void {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = null;
    if (next !== latest.current.value) latest.current.onChange(next);
  }

  function scheduleCommit(): void {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => commit(), COMMIT_DELAY_MS);
  }

  async function openMenu(source: string, caret: number, explicit: boolean): Promise<void> {
    const id = ++request.current;
    const w = wordAt(source, caret);
    if (!explicit && !w.prefix && !w.head) {
      setMenu(null);
      return;
    }
    const engine = await procgenWorkerEngine().catch(() => null);
    const fromEngine = await engineComplete(engine, completeRequest(file, path, source, caret, [file]));
    if (id !== request.current) return;
    const prefix = w.prefix.toLowerCase();
    const items = (fromEngine ?? localCompletions(doc, source, caret, outputName))
      .filter((c) => !prefix || c.label.toLowerCase().startsWith(prefix) || c.insert.toLowerCase().startsWith(prefix))
      .slice(0, MAX_ITEMS);
    setMenu(items.length ? { items, index: 0, start: w.start, end: w.end } : null);
  }

  function accept(item: Completion): void {
    const m = menu;
    const el = areaRef.current;
    if (!m) return;
    const next = text.slice(0, m.start) + item.insert + text.slice(m.end);
    setText(next);
    setMenu(null);
    scheduleCommit();
    const caret = m.start + item.insert.length;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }

  function scheduleHover(source: string, caret: number): void {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      void (async () => {
        const engine = await procgenWorkerEngine().catch(() => null);
        const fromEngine = await engineHover(engine, completeRequest(file, path, source, caret, [file]));
        if (fromEngine) {
          setHint(fromEngine);
          return;
        }
        // Offline: what the name at the caret is.
        const w = wordAt(source, caret);
        const word = source.slice(w.start, w.end);
        const known = doc ? scopeNames(doc).find((n) => n.name === word) : undefined;
        setHint(known ? `${known.kind} ${known.name}: ${known.detail}` : null);
      })();
    }, 250);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (menu) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        setMenu({ ...menu, index: (menu.index + delta + menu.items.length) % menu.items.length });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const item = menu.items[menu.index];
        if (item) accept(item);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setMenu(null);
        return;
      }
    }
    if (e.key === ' ' && e.ctrlKey) {
      e.preventDefault();
      void openMenu(text, e.currentTarget.selectionStart, true);
      return;
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      // Expressions are single values: Enter commits, Shift+Enter breaks the line.
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === 'Escape') {
      setText(value);
      e.currentTarget.blur();
    }
  }

  async function format(): Promise<void> {
    const engine = await procgenWorkerEngine().catch(() => null);
    const next = await engineFormat(engine, text);
    if (next !== text) {
      setText(next);
      commit(next);
    }
  }

  const fixes = own.flatMap((d) => (d.fixes ?? []).map((f) => ({ d, f })));

  return (
    <div className={`pcg-expr${worst ? ` has-${worst}` : ''}`} data-pcg-path={path}>
      <div className="pcg-expr-box">
        <pre className="pcg-expr-hl" ref={hlRef} aria-hidden="true">
          <Highlight text={text} marks={marks} />
        </pre>
        <textarea
          ref={areaRef}
          className="pcg-expr-input"
          value={text}
          rows={Math.min(6, Math.max(1, text.split('\n').length))}
          spellCheck={false}
          placeholder={placeholder}
          aria-label={path}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setMenu(null);
            commit();
          }}
          onScroll={(e) => {
            if (hlRef.current) {
              hlRef.current.scrollTop = e.currentTarget.scrollTop;
              hlRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
          onChange={(e) => {
            const next = e.target.value;
            const caret = e.target.selectionStart;
            setText(next);
            scheduleCommit();
            const typed = next[caret - 1] ?? '';
            if (/[A-Za-z0-9_.]/.test(typed)) void openMenu(next, caret, false);
            else setMenu(null);
          }}
          onKeyDown={onKeyDown}
          onKeyUp={(e) => scheduleHover(e.currentTarget.value, e.currentTarget.selectionStart)}
          onClick={(e) => scheduleHover(e.currentTarget.value, e.currentTarget.selectionStart)}
        />
        {menu && (
          <ul className="pcg-complete" role="listbox">
            {menu.items.map((item, i) => (
              <li
                key={`${item.label}-${i}`}
                role="option"
                aria-selected={i === menu.index}
                className={i === menu.index ? 'is-active' : ''}
                onMouseDown={(e) => {
                  e.preventDefault();
                  accept(item);
                }}
              >
                <span className={`pcg-complete-kind kind-${item.kind}`}>{item.kind.slice(0, 1).toUpperCase()}</span>
                <span className="pcg-complete-label">{item.label}</span>
                {item.detail && <span className="pcg-complete-detail">{item.detail}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="pcg-expr-tools">
        {onShow && (
          <button type="button" className="ibtn" title="Show where true (preview this expression)" aria-label="Show where true" onClick={() => onShow(text)}>
            <Icon name="eye" size={13} />
          </button>
        )}
        <button type="button" className="ibtn" title="Format (canonical printer)" aria-label="Format" onClick={() => void format()}>
          <Icon name="wand" size={13} />
        </button>
        {actions}
      </div>
      {focused && hint && <div className="pcg-expr-hint mono">{hint}</div>}
      {text === value &&
        own.map((d, i) => (
          <div key={i} className={`pcg-expr-msg is-${d.severity}`}>
            <span className="pcg-code">{d.code}</span> {d.message}
            {d.help && <span className="pcg-help"> — {d.help}</span>}
          </div>
        ))}
      {text === value && fixes.length > 0 && (
        <div className="pcg-fixes">
          {fixes.map(({ f }, i) => (
            <button
              key={i}
              type="button"
              className="pcg-fix"
              title={`Replace with ${f.replacement}`}
              onClick={() => {
                const next = applyFix(text, f);
                setText(next);
                commit(next);
              }}
            >
              <Icon name="wand" size={11} /> {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
